import { getPhoneValidationRule } from "../config/phoneValidation";
import utils from "./utils";

/**
 * 验证手机号是否符合指定国家的格式
 * @param phone 手机号
 * @param countryId 国家ID，如果不传则从本地存储获取
 * @returns 验证结果
 */
export const validatePhone = (
  phone: string,
  countryId?: string
): { valid: boolean; message?: string } => {
  if (!phone) {
    return { valid: false, message: "phone_required" };
  }

  // 获取国家ID
  const country = countryId || utils.getObject("countryId");

  // 获取对应国家的校验规则
  const rule = getPhoneValidationRule(country);

  if (!rule) {
    // 如果没有找到对应的校验规则，使用默认的通用格式校验
    // 默认: 0开头11位 或 非0开头10位
    const defaultPattern = /^(0\d{10}|[1-9]\d{9})$/;
    if (!defaultPattern.test(phone)) {
      return { valid: false, message: "invalid_phone_format" };
    }
    return { valid: true };
  }

  // 使用国家对应的正则表达式进行校验
  try {
    const regex = new RegExp(rule.regularExpression);
    if (!regex.test(phone)) {
      return { valid: false, message: "invalid_phone_format" };
    }
    return { valid: true };
  } catch {
    // 如果正则表达式解析失败，使用默认校验
    console.warn(
      `Invalid regex for country ${country}: ${rule.regularExpression}`
    );
    const defaultPattern = /^(0\d{10}|[1-9]\d{9})$/;
    if (!defaultPattern.test(phone)) {
      return { valid: false, message: "invalid_phone_format" };
    }
    return { valid: true };
  }
};

/**
 * 获取当前国家的手机号正则表达式
 * @param countryId 国家ID，如果不传则从本地存储获取
 * @returns 正则表达式对象
 */
/**
 * 默认正则：未配置国家或正则解析失败时使用
 * 支持国际格式（+国家码+号码）或本地格式（0/非0开头），与 Register/ResetPassword 中 fullPhone = countryCode + value 一致
 */
const DEFAULT_PHONE_PATTERN = /^(\+?\d{1,4})?\d{6,14}$/;

export const getPhonePattern = (countryId?: string): RegExp => {
  const country = countryId || utils.getObject("countryId");
  const rule = getPhoneValidationRule(country);

  if (!rule) {
    return DEFAULT_PHONE_PATTERN;
  }

  try {
    return new RegExp(rule.regularExpression);
  } catch {
    console.warn(
      `Invalid regex for country ${country}: ${rule.regularExpression}`
    );
    return DEFAULT_PHONE_PATTERN;
  }
};

/**
 * 处理手机号，去掉开头的0
 * @param phone 原始手机号
 * @returns 处理后的手机号
 */
export const processPhone = (phone: string): string => {
  if (!phone) return "";
  return phone.replace(/^0+/, "");
};

/**
 * 验证邮箱格式
 * @param email 邮箱地址
 * @returns 验证结果
 */
export const validateEmail = (
  email: string
): { valid: boolean; message?: string } => {
  if (!email) {
    return { valid: false, message: "email_required" };
  }

  // 避免使用 (?<!\.) 负向回顾，Safari 16.3 及以下不支持，会抛 "Invalid regular expression: invalid group specifier name"
  const emailPattern =
    /^[a-zA-Z0-9_-](?:[a-zA-Z0-9._-]*[a-zA-Z0-9_-])?@[a-zA-Z0-9-]+(\.[a-zA-Z0-9-]+)*\.[a-zA-Z]{2,}$/;
  if (!emailPattern.test(email)) {
    return { valid: false, message: "invalid_email_format" };
  }

  return { valid: true };
};

/**
 * 获取邮箱正则表达式
 * @returns 邮箱正则表达式
 */
export const getEmailPattern = (): RegExp => {
  return /^[a-zA-Z0-9_-](?:[a-zA-Z0-9._-]*[a-zA-Z0-9_-])?@[a-zA-Z0-9-]+(\.[a-zA-Z0-9-]+)*\.[a-zA-Z]{2,}$/;
};
